import { StatementCandidates, StatementDelivery } from '@/components/money/StatementCandidates'
import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import {
  Send,
  Eye,
  EyeOff,
  Download,
  FileText,
  Mail,
  Printer,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Clock3,
} from 'lucide-react'
import { toast } from 'sonner'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { InfoNotice } from '@/components/patterns/info-notice'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  CardFooter,
} from '@/components/ui/card'

import { IndexDataTable } from '@/components/tables/IndexTable'
import { Typography } from '@/components/ui/typography'
import { Badge } from '@/components/ui/badge'
import { StyledIcon } from '@/components/ui/styled-icon'
import { ActionRow } from '@/components/patterns/action-row'
import { MoneySummary } from '@/components/money/MoneySummary'
import dayjs from '@/lib/dayjs'
import { useOrganization } from '@/lib/services/organizations/organizations.query'
import { useAbility } from '@/lib/casl'
import { usePathHelper } from '@/hooks/usePathHelper'
import { formatCurrency, getErrorMessage } from '@/lib/utils'
import { adminTitleHead } from '@/lib/admin-route-title'
import { canViewStatements, canRunStatements } from '@/lib/services/money/money.access'
import { statementQueries } from '@/lib/services/money/statements.query'
import { statementSearchSchema } from '@/lib/services/money/statements.schema'
// oxlint-disable-next-line no-restricted-imports -- grandfathered
import { actOnStatementRun, downloadStatementRun } from '@/lib/services/money/statements.api'
import { downloadExportedReport } from '@/lib/download'
import { statementStatuses, summarizeStatementRun } from '@/lib/statement-status'

export const Route = createFileRoute('/_authenticated/$orgId/money/statements/')({
  component: StatementsPage,
  head: () => adminTitleHead('Customer Statements', 'Money'),
  validateSearch: (search) => statementSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, params, deps }) => {
    if (!canViewStatements(context.ability)) throw notFound()
    const { run_id, history_page = 1, ...filters } = deps.search
    if (run_id) {
      await context.queryClient.ensureQueryData(
        statementQueries.run({ organizationId: params.orgId, runId: run_id })
      )
    } else {
      await Promise.all([
        context.queryClient.ensureQueryData(
          statementQueries.candidates({ organizationId: params.orgId, filters })
        ),
        context.queryClient.ensureQueryData(
          statementQueries.runs({ organizationId: params.orgId, page: history_page })
        ),
      ])
    }
    return { isReadOnly: !canRunStatements(context.ability) }
  },
})

function StatementsPage() {
  const { orgId } = Route.useParams()
  const { run_id, history_page, ...filters } = Route.useSearch()
  const canRun = canRunStatements(useAbility())
  const path = usePathHelper()
  const navigate = useNavigate()
  const go = (search) =>
    navigate({
      ...path.statements.index().using({ orgId }).link,
      search: { history_page, ...search },
      resetScroll: search.run_id !== run_id,
    })
  return (
    <div className="space-y-5 pb-6">
      <AdminPageHeader
        icon={Send}
        title="Customer Statements"
        description="Review open invoices, then email or print statements together."
      />
      {run_id ? (
        <StatementRun
          key={run_id}
          orgId={orgId}
          runId={run_id}
          canRun={canRun}
          onBack={() => go(filters)}
        />
      ) : (
        <StatementCandidates
          key={JSON.stringify({ ...filters, page: undefined, delivery_method: undefined })}
          orgId={orgId}
          filters={filters}
          canRun={canRun}
          go={go}
        />
      )}
      {!run_id && (
        <RecentRuns
          orgId={orgId}
          page={history_page}
          onPageChange={(page) => go({ ...filters, history_page: page })}
          onChoose={(id) => go({ ...filters, run_id: id })}
        />
      )}
    </div>
  )
}
function StatementRun({ orgId, runId, canRun, onBack }) {
  const path = usePathHelper()
  // TanStack treats cell functions as component types; keep them stable across polling.
  const columns = useMemo(
    () => [
      {
        enableSorting: false,
        accessorKey: 'customer_name',
        header: 'Customer',
        cell: ({ row, getValue }) =>
          row.original.customer_id ? (
            <Typography
              as={Link}
              variant="labelStrong"
              tone="interaction"
              interaction="hoverUnderline"
              {...path.customers.show(row.original.customer_id).using({ orgId }).link}
            >
              {getValue()}
            </Typography>
          ) : (
            getValue()
          ),
        meta: { minWidth: '12rem', label: 'Customer', kind: 'primary' },
      },
      {
        enableSorting: false,
        accessorKey: 'total_due',
        header: 'Total due',
        cell: ({ getValue }) => (
          <Typography variant="labelStrong" className="tabular-nums">
            {formatCurrency(getValue())}
          </Typography>
        ),
        meta: { align: 'end', label: 'Total due', kind: 'money' },
      },
      {
        id: 'delivery',
        enableSorting: false,
        header: 'Delivery',
        meta: { label: 'Delivery', kind: 'text' },
        cell: ({ row: { original: item } }) => (
          <StatementDelivery
            method={item.method}
            destination={item.destination}
            missingEmail={item.missing_email}
          />
        ),
      },
      {
        enableSorting: false,
        accessorKey: 'status',
        header: 'Status',
        meta: { label: 'Status', kind: 'badge' },
        cell: ({ row: { original: item } }) => (
          <div className="space-y-1">
            <Badge variant={statementStatuses[item.status]?.variant}>
              {statementStatuses[item.status]?.label || item.status}
            </Badge>
            {item.error && (
              <Typography variant="description" tone="destructive">
                {item.error}
              </Typography>
            )}
          </div>
        ),
      },
    ],
    [path, orgId]
  )
  const { data: organization } = useOrganization(orgId)
  const options = statementQueries.run({ organizationId: orgId, runId })
  const { data: run } = useSuspenseQuery(options)
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const args = { organizationId: orgId, runId }
  const act = async (action) => {
    setBusy(true)
    try {
      queryClient.setQueryData(options.queryKey, await actOnStatementRun({ ...args, action }))
      void queryClient.invalidateQueries(statementQueries.runs({ organizationId: orgId }))
    } catch (error) {
      toast.error('Statement run could not be updated', { description: getErrorMessage(error) })
    } finally {
      setBusy(false)
    }
  }
  const download = async () => {
    setBusy(true)
    try {
      downloadExportedReport(await downloadStatementRun(args), `customer-statements-${runId}.pdf`)
    } catch (error) {
      toast.error('Print packet could not be prepared', { description: getErrorMessage(error) })
    } finally {
      setBusy(false)
    }
  }
  const [showPreview, setShowPreview] = useState(false)
  const preview = useQuery({
    ...statementQueries.preview({ organizationId: orgId, runId }),
    enabled: showPreview,
  })
  const emails = run.items.filter((item) => item.method === 'email').length
  const prints = run.items.length - emails
  const fallbackCount = run.items.filter((item) => item.missing_email).length
  const summary = summarizeStatementRun(run)
  const { failed, sent, ready_to_print: ready, printed, queued, processing } = summary.counts
  const pending = queued + processing
  const title = {
    prepared: 'Review your statement batch',
    failed: 'Statements failed to send',
    needs_attention: 'Some statements need attention',
    queued: 'Your emails are queued',
    processing: 'Your emails are sending',
    ready_to_print: 'Your print packet is ready',
    printed: 'Statements printed',
    sent: 'Statements sent',
    complete: 'Statement run complete',
    in_progress: 'Statement run in progress',
  }[summary.status]
  return (
    <div className="@container/run space-y-5">
      <Button variant="ghost" onClick={onBack}>
        <ArrowLeft />
        Back to customer selection
      </Button>
      <MoneySummary
        title={title}
        description={`Run #${run.id} · Balances as of ${dayjs(run.cutoff).tz(organization.timezone).format('MMM D, YYYY [at] h:mm A z')}`}
        metrics={[
          {
            title: 'Statements',
            value: run.items.length,
            description: 'Saved and ready to review',
            icon: FileText,
            tone: 'brand',
          },
          {
            title: run.dispatched_at ? 'Emails sent' : 'By email',
            value: run.dispatched_at ? sent : emails,
            description: pending ? `${pending} still sending` : `${emails} email destinations`,
            icon: Mail,
            tone: 'info',
          },
          {
            title: printed ? 'Printed' : 'To print',
            value: printed || prints,
            description: ready ? `${ready} ready to print` : 'One combined print packet',
            icon: Printer,
            tone: 'warning',
          },
          {
            title: failed ? 'Need attention' : 'Missing email',
            value: failed || fallbackCount,
            description: failed ? 'Retry only the failed emails' : 'Included in the print packet',
            icon: failed ? AlertCircle : CheckCircle2,
            tone: failed ? 'destructive' : 'success',
          },
        ]}
      />
      {fallbackCount > 0 && (
        <InfoNotice tone="warning" title={`${fallbackCount} customers have no email address`}>
          Their statements are included in the print packet. Review the delivery methods below
          before running.
        </InfoNotice>
      )}
      <Card size="flush" className="overflow-hidden">
        <CardHeader className="py-5">
          <CardTitle size="sm">
            {run.dispatched_at ? 'Statement status' : 'Review recipients'}
          </CardTitle>
          <CardDescription>
            {run.dispatched_at
              ? 'See which statements were sent, printed, or still need attention.'
              : 'Check each destination before sending. Later payments will not change these documents.'}
          </CardDescription>
        </CardHeader>
        <IndexDataTable data={run.items} columns={columns} />
      </Card>
      {showPreview && (
        <Card surface="brandSubtle">
          <CardHeader>
            <div className="flex flex-wrap justify-between gap-3">
              <div>
                <CardTitle size="sm">Statement preview</CardTitle>
                <CardDescription>
                  Exactly as prepared. Payment links always show the current balance due.
                </CardDescription>
              </div>
              <Button variant="outline" onClick={() => setShowPreview(false)}>
                <EyeOff />
                Hide preview
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {preview.isPending ? (
              <Typography variant="description">Loading statement preview…</Typography>
            ) : preview.isError ? (
              <InfoNotice tone="warning" title="Preview unavailable">
                {getErrorMessage(preview.error)}
              </InfoNotice>
            ) : (
              <iframe
                title="Statement preview"
                srcDoc={preview.data}
                sandbox=""
                className="h-[760px] w-full rounded-lg border"
              />
            )}
          </CardContent>
        </Card>
      )}
      <Card size="sm" surface="brandSubtle" className="sticky bottom-3 z-10">
        <CardContent className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Typography variant="labelStrong">
              {run.items.length} statements · {emails} email · {prints} print
            </Typography>
            <Typography variant="caption" as="p">
              {ready
                ? 'Download the packet, then mark it printed when you’re done.'
                : 'One document per customer. Print copies are grouped in one packet.'}
            </Typography>
          </div>
          <div className="flex flex-wrap gap-2">
            {!showPreview && (
              <Button variant="outline" onClick={() => setShowPreview(true)}>
                <Eye />
                Preview statements
              </Button>
            )}
            {!run.dispatched_at && (
              <Button disabled={!canRun || busy} onClick={() => act('dispatch')}>
                <Send />
                Run statements
              </Button>
            )}
            {prints > 0 && run.dispatched_at && (
              <Button disabled={busy} onClick={download}>
                <Download />
                Download print packet
              </Button>
            )}
            {ready > 0 && (
              <Button variant="outline" disabled={!canRun || busy} onClick={() => act('printed')}>
                <CheckCircle2 />
                Mark print packet as printed
              </Button>
            )}
            {failed > 0 && (
              <Button variant="outline" disabled={!canRun || busy} onClick={() => act('retry')}>
                Retry failed emails
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function RecentRuns({ orgId, page, onPageChange, onChoose }) {
  const { data: organization } = useOrganization(orgId)
  const { data } = useSuspenseQuery(statementQueries.runs({ organizationId: orgId, page }))
  if (!data.total) return null
  return (
    <Card size="flush">
      <CardHeader className="py-5">
        <div className="flex items-center gap-3">
          <StyledIcon icon={Clock3} tone="brand" />
          <div>
            <CardTitle size="sm">Recent statement runs</CardTitle>
            <CardDescription>Review past statements and check their status.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="divide-y pb-3">
        {data.data.map((run) => {
          const summary = summarizeStatementRun(run)
          return (
            <ActionRow key={run.id} onClick={() => onChoose(run.id)} className="gap-3">
              <StyledIcon icon={FileText} tone="brand" size="sm" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <Typography variant="labelStrong">Run #{run.id}</Typography>
                  <Badge variant={summary.variant}>{summary.label}</Badge>
                </div>
                <Typography as="p" variant="caption">
                  As of {dayjs(run.cutoff).tz(organization.timezone).format('MMM D, YYYY')} ·{' '}
                  {run.items_count} customers
                </Typography>
                {summary.description && (
                  <Typography as="p" variant="caption">
                    {summary.description}
                  </Typography>
                )}
              </div>
              <ArrowRight className="size-4 shrink-0" />
            </ActionRow>
          )
        })}
      </CardContent>
      {data.last_page > 1 && (
        <CardFooter className="flex flex-wrap justify-between gap-3 py-4">
          <Typography variant="caption">
            Page {data.current_page} of {data.last_page} · {data.total} runs
          </Typography>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={data.current_page <= 1}
              onClick={() => onPageChange(data.current_page - 1)}
            >
              Newer runs
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={data.current_page >= data.last_page}
              onClick={() => onPageChange(data.current_page + 1)}
            >
              Older runs
            </Button>
          </div>
        </CardFooter>
      )}
    </Card>
  )
}
